import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { CategoryField, RoleStageFields } from './ProfileFields';
import AutoTextarea from './AutoTextarea';
import {
  CountryInput, TimezoneSelect, countryFor, countryPatch, deviceTimezone,
} from './PlaceFields';
import { LIMITS } from '../lib/limits';
import Loader from './Loader';

/* First run. Shown once per member (the server remembers, not the browser),
   finished or skipped.

   It asks for the least that makes introductions work — who you are, and what
   you want — and leaves the rest of the profile for later. Each step saves when
   you move on, so closing the window halfway loses nothing. */

const INTENT_KEYS = ['says_yes_to', 'seeking', 'offering'];
const STEPS = 3;

export default function Onboarding({ onDone, onOpenProfile }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [intent, setIntent] = useState({ says_yes_to: '', seeking: '', offering: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.me()
      .then((me) => {
        // A default the member can see and change beats an empty box they skip.
        setProfile({ ...me, timezone_label: me.timezone_label || deviceTimezone() });
        setIntent({
          says_yes_to: me.intent?.says_yes_to || '',
          seeking: me.intent?.seeking || '',
          offering: me.intent?.offering || '',
        });
      })
      .catch((e) => setError(e.message));
  }, []);

  if (!profile) {
    return error ? null : (
      <div className="ob-overlay"><div className="ob-card"><Loader /></div></div>
    );
  }

  const edit = (patch) => setProfile((p) => ({ ...p, ...patch }));
  const text = (key) => ({ value: profile[key] || '', onChange: (e) => edit({ [key]: e.target.value }) });
  const intentText = (key) => ({
    value: intent[key],
    onChange: (e) => setIntent((i) => ({ ...i, [key]: e.target.value })),
  });

  async function saveStep() {
    if (step === 1) {
      await api.updateMe({
        headline: profile.headline || '',
        location: profile.location || '',
        timezone_label: profile.timezone_label || '',
        role_tags: profile.role_tags || [],
        stage: profile.stage || '',
        stages_served: profile.stages_served || [],
        categories: profile.categories || [],
      });
    }
    if (step === 2) {
      const filled = Object.fromEntries(INTENT_KEYS.filter((k) => intent[k].trim()).map((k) => [k, intent[k].trim()]));
      if (Object.keys(filled).length) await api.updateIntent(filled);
    }
  }

  async function run(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const next = () => run(async () => {
    await saveStep();
    setStep((s) => s + 1);
  });

  // Skipping still keeps whatever was typed on this step.
  const finish = (then) => run(async () => {
    await saveStep();
    await api.updateMe({ onboarded: true });
    onDone();
    then?.();
  });

  return (
    <div className="ob-overlay" role="dialog" aria-modal="true" aria-labelledby="ob-title">
      <div className="ob-card">
        {step > 0 && step <= STEPS - 1 && (
          <p className="ob-step">Step {step} of {STEPS - 1}</p>
        )}

        {step === 0 && (
          <>
            <h2 id="ob-title">Welcome{profile.name ? `, ${profile.name.split(' ')[0]}` : ''}</h2>
            <p className="ob-lede">
              The network reads your profile and introduces you to the few people worth
              meeting, and helps you find the right ones yourself — so <b>the more specific your profile, the better
              your introductions.</b>
            </p>
            <p className="ob-lede">
              Two short steps, about three minutes. Everything saves as you go, and you can
              always finish later under <b>My profile</b>.
            </p>
          </>
        )}

        {step === 1 && (
          <>
            <h2 id="ob-title">Who you are</h2>
            <p className="ob-lede">Used to decide who you are worth introducing to.</p>
            <div className="pe-field">
              <label className="flabel">Headline</label>
              <input className="in" {...text('headline')} maxLength={LIMITS.headline} placeholder="e.g. Fractional COO & GTM advisor" />
              <p className="pe-hint">One line. The longer story goes in your full profile.</p>
            </div>
            <div className="pe-grid ob-grid">
              <div className="pe-field">
                <label className="flabel">Country</label>
                <CountryInput
                  id="ob-location"
                  value={profile.location}
                  placeholder="Search your country"
                  onPick={(c) => edit(countryPatch(c, profile.timezone_label))}
                />
              </div>
              <div className="pe-field">
                <label className="flabel">Timezone</label>
                <TimezoneSelect
                  id="ob-timezone"
                  value={profile.timezone_label}
                  country={countryFor(profile.location)}
                  onChange={(v) => edit({ timezone_label: v })}
                />
              </div>
            </div>
            <RoleStageFields profile={profile} onChange={edit} />
            <CategoryField profile={profile} onChange={edit} />
          </>
        )}

        {step === 2 && (
          <>
            <h2 id="ob-title">What you want from this network</h2>
            <p className="ob-lede">
              This is what the matcher actually reads. One honest sentence each beats a paragraph.
            </p>
            <div className="pe-field">
              <label className="flabel">A conversation you'd say yes to right now</label>
              <AutoTextarea className="ta" {...intentText('says_yes_to')}
                placeholder="e.g. 30 minutes with a Web3 founder whose MVP has users but no paying customers yet." />
              <p className="pe-hint">One specific conversation, not a type of client — the matcher leans on this more than anything else.</p>
            </div>
            <div className="pe-field">
              <label className="flabel">Seeking</label>
              <AutoTextarea className="ta" {...intentText('seeking')}
                placeholder="e.g. Growth-stage founders who need an interim COO" />
            </div>
            <div className="pe-field">
              <label className="flabel">Offering</label>
              <AutoTextarea className="ta" {...intentText('offering')}
                placeholder="e.g. Go-to-market and operations for fintech and web3 teams" />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 id="ob-title">You're set up</h2>
            <p className="ob-lede">
              That is enough for introductions to start. The rest of your profile — what you're
              building, links that back it up, and how many introductions you want a week — is
              under <b>My profile</b> whenever you have a minute.
            </p>
          </>
        )}

        {error && <div className="pe-error">{error}</div>}

        <div className="ob-actions">
          {step < 3 && (
            <button className="btn quiet" onClick={() => finish()} disabled={busy}>
              {step === 0 ? 'Skip for now' : 'Finish later'}
            </button>
          )}
          {step > 0 && step < 3 && (
            <button className="btn ghost" onClick={() => setStep((s) => s - 1)} disabled={busy}>Back</button>
          )}
          {step < 3 && (
            <button className="btn primary" onClick={next} disabled={busy}>
              {busy ? 'Saving…' : step === 0 ? "Let's start" : 'Next'}
            </button>
          )}
          {step === 3 && (
            <>
              <button className="btn ghost" onClick={() => finish(onOpenProfile)} disabled={busy}>
                Open my full profile
              </button>
              <button className="btn primary" onClick={() => finish()} disabled={busy}>
                Go to Home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
